import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Users, Trash2, Pencil, UserPlus, LogOut, Loader2, X as XIcon, Check,
    Plus, ChevronDown, ChevronRight, Search,
} from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import { contactInitials } from '../../utils/avatar';
import apiService from '../../services/apiService';
import contactService from '../../services/contactService';
import teamService from '../../services/teamService';
import { PillarBar } from './InteractionBar.jsx';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

// The two team roles — the SAME vocabulary Boards and Books use, because a team
// role is carried straight onto the membership row a team share mints
// ("Update team creation part 2"). Kept in lock-step with
// team_service.VALID_MEMBER_ROLES.
export const TEAM_ROLES = ['member', 'owner'];
export const DEFAULT_TEAM_ROLE = 'member';

// Accent- + case-insensitive fold, mirroring the BE's strip_accents (NFD, drop
// combining marks, casefold) so "Jose" finds "José" in the member search exactly
// as it would server-side. The explicit combining range is used instead of
// \p{Diacritic} — same result, no Unicode-property-escape dependency.
export const foldText = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

// A contact matches the member search on its NAME or ANY of its emails (the
// primary plus every extra one — a contact saved under a nickname is still
// findable by the address the user actually knows).
export const contactMatchesQuery = (contact, query) => {
    const q = foldText(query);
    if (!q) return true;
    const extras = Array.isArray(contact?.emails) ? contact.emails : [];
    return [contact?.name, contact?.email, ...extras]
        .some((field) => field && foldText(field).includes(q));
};

const emitTeamChanged = (detail) => {
    try { window.dispatchEvent(new CustomEvent('omnis:team-changed', { detail })); } catch (_) { /* ignore */ }
};

const notifyContactChanged = () => {
    try { window.dispatchEvent(new CustomEvent('omnis:contact-changed')); } catch (_) { /* ignore */ }
};

// Uniform initials ball: white bg / black text / thin border (New Design #72).
const Ball = ({ label }) => (
    <div className="w-8 h-8 rounded-full bg-white border border-pai text-black flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
        {contactInitials(label)}
    </div>
);

// Compact Owner/Member picker used while composing a team's roster. A native
// <select> on purpose: it is a form CONTROL, so it stays keyboard/screen-reader
// correct, needs no portal inside the dialog's scroll container, and carries no
// inline styling (the #303 / "Team Name" specs assert that no decorative,
// inline-coloured node exists anywhere in this dialog).
const RoleSelect = ({ testId, value, onChange, label }) => {
    const { t } = useLanguage();
    return (
        <select
            data-testid={testId}
            data-role={value}
            value={value}
            aria-label={label}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onChange(e.target.value)}
            className="flex-shrink-0 text-[11px] px-1.5 py-1 rounded-chip border border-pai bg-white/[0.03] text-pai-secondary"
        >
            <option value="member">{t('teamRoleMember')}</option>
            <option value="owner">{t('teamRoleOwner')}</option>
        </select>
    );
};

// ── A single team member row (the PRIVACY BARRIER lives here) ───────────────
// A member the VIEWER shares a ContactLink with (their user_id ∈ linkedUserIds),
// or the viewer themselves, is shown full-profile style (username + email). A
// NON-linked co-member is shown initials + EMAIL ONLY + a blue [Invite] shortcut
// (T7): the viewer never learns a stranger's name pre-handshake.
const TeamMemberRow = ({ member, linkedIds, currentUserId, canKick, onKick, busy }) => {
    const { t } = useLanguage();
    const [inviteState, setInviteState] = useState(null); // null | 'sending' | 'sent' | 'error'
    const isSelf = !!currentUserId && member.user_id === currentUserId;
    const isLinked = isSelf || (Array.isArray(linkedIds) ? linkedIds.includes(member.user_id) : false);
    const email = member.email || '';
    const displayName = isLinked ? (member.username || email || member.user_id) : email;

    const invite = async () => {
        if (!email) return;
        setInviteState('sending');
        try {
            await contactService.sendRequest(email);
            setInviteState('sent');
            notifyContactChanged();
        } catch (err) {
            // they_requested_you / already_requested / already_connected all mean a
            // handshake is in flight or done → treat as "sent" (idempotent affordance).
            const code = err?.message || '';
            if (/already|requested/i.test(code)) setInviteState('sent');
            else setInviteState('error');
        }
    };

    return (
        <div
            data-testid="team-member-row"
            data-member-linked={isLinked ? '1' : '0'}
            className="flex items-center gap-2.5 px-2 py-1.5 rounded-row hover:bg-white/[0.05]"
        >
            <Ball label={displayName} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-white truncate" title={displayName}>
                        {displayName}
                    </span>
                    {/* The CREATOR chip is the product's one blue-tint chip — the same
                        treatment Settings' ACCOUNT row uses, and what the Contacts
                        source screen shows beside a team creator. */}
                    {member.is_creator && (
                        <span className="text-[9px] uppercase tracking-[0.06em] font-bold px-1.5 py-0.5 rounded-chip bg-[rgba(74,144,240,0.16)] text-pai-blue-tint">
                            {t('teamRoleCreator')}
                        </span>
                    )}
                    {!member.is_creator && member.role === 'owner' && (
                        <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-chip bg-white/[0.08] text-pai-secondary">
                            {t('teamRoleOwner')}
                        </span>
                    )}
                </div>
                {isLinked && email && (
                    <div className="text-[11px] text-pai-meta truncate">{email}</div>
                )}
                {!isLinked && (
                    <div className="text-[11px] text-pai-faint">{t('teamMemberStranger')}</div>
                )}
            </div>

            {!isLinked && !isSelf && email && (
                inviteState === 'sent' ? (
                    <span data-testid="team-member-invite-sent" className="text-[11px] px-2 py-1 rounded-full bg-white/[0.06] text-pai-meta flex-shrink-0">
                        {t('contactRequestSent')}
                    </span>
                ) : (
                    <button
                        data-testid="team-member-invite"
                        onClick={invite}
                        disabled={inviteState === 'sending'}
                        className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-pai-blue border border-pai-blue/40 bg-pai-blue/[0.1] hover:bg-pai-blue/[0.18] disabled:opacity-50 rounded-full flex-shrink-0 transition-colors"
                        title={t('teamInviteMember')}
                    >
                        {inviteState === 'sending' ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
                        {t('teamInviteMember')}
                    </button>
                )
            )}

            {canKick && !member.is_creator && !isSelf && (
                <button
                    data-testid="team-member-kick"
                    onClick={() => onKick(member)}
                    disabled={busy}
                    className="p-1.5 text-pai-faint hover:text-pai-red rounded-ctl flex-shrink-0 disabled:opacity-40 transition-colors"
                    title={t('teamKickMember')}
                    aria-label={t('teamKickMember')}
                >
                    <XIcon size={14} />
                </button>
            )}
        </div>
    );
};

// ── Add-member picker (owner control) — NETWORK contacts only, minus current ──
const AddMemberPicker = ({ networkContacts, memberUserIds, onAdd, busy }) => {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    const candidates = useMemo(() => (networkContacts || []).filter(
        (c) => c.omnis_user_id && !memberUserIds.includes(c.omnis_user_id),
    ), [networkContacts, memberUserIds]);

    return (
        <div className="relative">
            <button
                data-testid="team-add-member"
                onClick={() => setOpen((v) => !v)}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-white/[0.05] border border-pai hover:bg-white/[0.1] rounded-ctl disabled:opacity-50 transition-colors"
            >
                <UserPlus size={13} /> {t('teamAddMember')}
            </button>
            {open && (
                <div data-testid="team-add-member-menu" className="absolute left-0 top-9 z-30 w-56 max-h-60 overflow-y-auto bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1">
                    {candidates.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-pai-faint italic">{t('teamAddMemberEmpty')}</p>
                    ) : candidates.map((c, i) => (
                        <button
                            key={c.id}
                            data-testid={`team-add-member-option-${i}`}
                            onClick={() => { setOpen(false); onAdd(c); }}
                            className="w-full text-left px-3 py-2 text-xs text-pai-secondary hover:bg-white/[0.07] flex items-center gap-2 truncate"
                            title={c.name}
                        >
                            <Ball label={c.name} />
                            <span className="truncate">{c.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Delete-team confirm modal (creator only — Smithereens) ──────────────────
const TeamDeleteConfirmModal = ({ team, onClose, onConfirm, busy }) => {
    const { t } = useLanguage();
    return (
        <Modal onClose={onClose} zIndex="z-[90]" size="sm" cardClassName="p-5" cardTestId="team-delete-confirm">
                <div className="flex items-center gap-2 mb-3 text-pai-red">
                    <Trash2 size={16} />
                    <h3 className="text-base font-semibold">{t('teamDeleteConfirmTitle', { name: team.name })}</h3>
                </div>
                <p className="text-sm text-pai-secondary mb-4">{t('teamDeleteDisclaimer')}</p>
                <div className="flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-3 py-1.5 text-sm text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded-chip disabled:opacity-50"
                    >
                        {t('cardCancel')}
                    </button>
                    <button
                        data-testid="team-delete-confirm-btn"
                        onClick={onConfirm}
                        disabled={busy}
                        className="pai-btn-danger disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        {t('teamDeleteConfirmBtn')}
                    </button>
                </div>
            </Modal>
    );
};

// ── A single expandable team row ────────────────────────────────────────────
export const TeamRow = ({
    team, expanded, onToggle, networkContacts, linkedIds, currentUserId,
    navigate, onPreviewDoc, onChanged,
}) => {
    const { t } = useLanguage();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [nameDraft, setNameDraft] = useState(team.name);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const isOwner = team.viewer_role === 'owner';
    const isCreator = !!team.is_creator;

    const loadDetail = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await teamService.getTeam(team.id);
            setDetail(res.team || res);
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
    }, [team.id, t]);

    useEffect(() => { if (expanded) loadDetail(); }, [expanded, loadDetail]);

    // A chat-tool / another surface mutated teams while this one is open → refresh.
    useEffect(() => {
        if (!expanded) return undefined;
        const handler = () => loadDetail();
        window.addEventListener('omnis:team-changed', handler);
        return () => window.removeEventListener('omnis:team-changed', handler);
    }, [expanded, loadDetail]);

    const members = useMemo(() => detail?.members || [], [detail?.members]);
    const memberUserIds = useMemo(() => members.map((m) => m.user_id), [members]);
    const shares = detail?.shares || {};

    const afterMutation = useCallback(async () => {
        await loadDetail();
        emitTeamChanged({ team_id: team.id });
        onChanged?.();
    }, [loadDetail, team.id, onChanged]);

    const handleAddMember = async (contact) => {
        setBusy(true);
        setError(null);
        try {
            await teamService.addMember(team.id, { userId: contact.omnis_user_id });
            await afterMutation();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    const handleKick = async (member) => {
        setBusy(true);
        setError(null);
        try {
            await teamService.removeMember(team.id, member.user_id);
            await afterMutation();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    const handleLeave = async () => {
        setBusy(true);
        setError(null);
        try {
            // NEVER send a null member id (teams.spec.js 5b regression: a missing
            // currentUserId used to fire DELETE /api/teams/<id>/members/null → 404).
            // If the threaded id hasn't resolved yet, fetch the profile once — it
            // returns the viewer's DB id.
            let uid = currentUserId;
            if (!uid) {
                const profile = await apiService.getUserProfile().catch(() => null);
                uid = profile?.id || null;
            }
            if (!uid) throw new Error(t('teamLeaveFailed'));
            await teamService.removeMember(team.id, uid);
            emitTeamChanged({ team_id: team.id, left: true });
            onChanged?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        setBusy(true);
        setError(null);
        try {
            await teamService.deleteTeam(team.id);
            setConfirmDelete(false);
            emitTeamChanged({ team_id: team.id, deleted: true });
            onChanged?.();
        } catch (err) {
            setError(userErrorMessage(err, t));
            setBusy(false);
        }
    };

    const saveEdit = async () => {
        const name = nameDraft.trim();
        if (!name) { setError(t('teamNameRequired')); return; }
        setBusy(true);
        setError(null);
        try {
            await teamService.updateTeam(team.id, { name });
            setEditing(false);
            await afterMutation();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border-b border-pai-hairline" data-team-id={team.id}>
            <div
                role="button"
                tabIndex={0}
                data-testid="team-row"
                onClick={onToggle}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left cursor-pointer hover:bg-white/[0.05] transition-colors ${expanded ? 'bg-white/[0.055]' : ''}`}
            >
                {/* NOTHING between the disclosure chevron and the name: no dot, no
                    icon, no placeholder ("Team Name"). #303 removed the colour
                    picker from creation, so every team created since stores NULL
                    and this render fell back to a neutral `#94a3b8` swatch — a
                    grey ball that carried no information and read as a defect. */}
                {expanded ? <ChevronDown size={14} className="text-pai-faint flex-shrink-0" /> : <ChevronRight size={14} className="text-pai-faint flex-shrink-0" />}
                <span data-testid="team-row-name" className="flex-1 min-w-0 text-sm font-medium text-white truncate">
                    {team.name}
                </span>
                <span data-testid="team-member-count" className="flex items-center gap-1 text-[13px] px-2 py-0.5 rounded-full border border-pai-subtle bg-white/[0.06] text-pai-secondary tnum flex-shrink-0">
                    <Users size={10} /> {team.member_count ?? memberUserIds.length}
                </span>
            </div>

            {expanded && (
                <div data-testid="team-expanded" className="px-3 pb-3 pt-1 space-y-2 bg-white/[0.02]">
                    {error && (
                        <div className="text-[11px] text-pai-red">{error}</div>
                    )}

                    {/* Owner: inline rename. The colour swatches were removed with the
                        team colour marker (Team Name bug) — a colour rendered nowhere,
                        so picking one did nothing. `Team.color` stays in the schema/API. */}
                    {isOwner && (
                        editing ? (
                            <div data-testid="team-edit-panel" className="flex items-center gap-2 flex-wrap bg-white/[0.03] rounded-row border border-pai-subtle p-2">
                                <input
                                    data-testid="team-rename-input"
                                    value={nameDraft}
                                    onChange={(e) => setNameDraft(e.target.value)}
                                    className="flex-1 min-w-[120px] px-2 py-1 text-sm pai-input"
                                />
                                <button
                                    data-testid="team-rename-save"
                                    onClick={saveEdit}
                                    disabled={busy}
                                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-pai-void bg-white hover:bg-white/[0.88] disabled:opacity-50 rounded-chip transition-colors"
                                >
                                    <Check size={12} /> {t('editSaveBtn')}
                                </button>
                                <button
                                    onClick={() => { setEditing(false); setNameDraft(team.name); }}
                                    disabled={busy}
                                    className="px-2 py-1 text-xs text-pai-secondary hover:bg-white/[0.07] rounded-chip disabled:opacity-50"
                                >
                                    {t('cardCancel')}
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <AddMemberPicker
                                    networkContacts={networkContacts}
                                    memberUserIds={memberUserIds}
                                    onAdd={handleAddMember}
                                    busy={busy}
                                />
                                <button
                                    data-testid="team-edit-btn"
                                    onClick={() => { setNameDraft(team.name); setEditing(true); }}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded-chip"
                                >
                                    <Pencil size={12} /> {t('teamEditBtn')}
                                </button>
                            </div>
                        )
                    )}

                    {/* (1) Member list — the privacy barrier. */}
                    <section data-testid="team-members" className="bg-white/[0.03] rounded-row border border-pai-subtle p-1.5">
                        {loading && !detail ? (
                            <div className="flex items-center justify-center py-3 text-pai-faint"><Loader2 size={16} className="animate-spin" /></div>
                        ) : members.length === 0 ? (
                            <p className="px-2 py-2 text-xs text-pai-faint italic">{t('teamNoMembers')}</p>
                        ) : members.map((m) => (
                            <TeamMemberRow
                                key={m.user_id}
                                member={m}
                                linkedIds={linkedIds}
                                currentUserId={currentUserId}
                                canKick={isOwner}
                                onKick={handleKick}
                                busy={busy}
                            />
                        ))}
                    </section>

                    {/* (2) The 4 team pillars — same drawer pattern as the contact Interaction Bar. */}
                    <PillarBar
                        testIds={{
                            bar: 'team-pillars',
                            docs: 'team-pillar-docs', chats: 'team-pillar-chats',
                            events: 'team-pillar-events', financials: 'team-pillar-financials',
                            drawerDocs: 'team-pillar-drawer-docs', drawerChats: 'team-pillar-drawer-chats',
                            drawerEvents: 'team-pillar-drawer-events', drawerFinancials: 'team-pillar-drawer-financials',
                        }}
                        docs={shares.document || []}
                        convs={shares.conversation || []}
                        events={shares.event || []}
                        books={shares.book || []}
                        loading={loading}
                        navigate={navigate}
                        onPreviewDoc={onPreviewDoc}
                    />

                    {/* (3) Membership controls — Leave (non-creator) / Delete (creator). */}
                    <div className="flex items-center justify-end gap-2 pt-1">
                        {!isCreator && (
                            <button
                                data-testid="team-leave-btn"
                                onClick={handleLeave}
                                disabled={busy}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded-chip disabled:opacity-50"
                            >
                                <LogOut size={12} /> {t('teamLeaveBtn')}
                            </button>
                        )}
                        {isCreator && (
                            <button
                                data-testid="team-delete-btn"
                                onClick={() => setConfirmDelete(true)}
                                disabled={busy}
                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-pai-red border border-pai-red/40 bg-pai-red/[0.08] hover:bg-pai-red/[0.16] rounded-ctl disabled:opacity-50 transition-colors"
                            >
                                <Trash2 size={12} /> {t('teamDeleteBtn')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {confirmDelete && (
                <TeamDeleteConfirmModal
                    team={team}
                    busy={busy}
                    onClose={() => setConfirmDelete(false)}
                    onConfirm={handleDelete}
                />
            )}
        </div>
    );
};

// ── Create-team dialog (Update team creation #303 + part 2) ─────────────────
// EXACTLY THREE primary inputs: (1) Team Name (required), (2) Description
// (optional), (3) Select Members + the roster-confirm step, which IS the
// member-selection review. There is deliberately NO colour picker / hex
// selection here: `Team.color` still exists in the schema + API and the EDIT
// flow (TeamRow) still sets it, but creation omits it entirely and the server
// stores NULL, which every team render already falls back on.
//
// PART 2 adds two things INSIDE input (3), not a fourth input:
//   • a SEARCH box over the network — the list used to be a static scroll of
//     every linked contact, which stops being usable the moment the network is
//     more than a screenful. Matching is accent- + case-insensitive on the name
//     AND every email (`contactMatchesQuery`).
//   • a ROLE per selected contact (Member default, Owner opt-in), shown the
//     instant someone is ticked and editable again on the roster step. The role
//     is not decoration: the BE carries it onto the membership rows a later
//     Board/Book team share mints, so it triggers the privileges those modules
//     already define.
// Selection state is INDEPENDENT of the search text — filtering only changes
// what is listed, never who is on the roster, so a user can search, tick, search
// again and still confirm everyone they picked.
export const TeamCreateDialog = ({ open, networkContacts, onClose, onCreated }) => {
    const { t } = useLanguage();
    const [step, setStep] = useState('form'); // 'form' | 'confirm'
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selected, setSelected] = useState(() => new Set());
    const [roles, setRoles] = useState(() => ({}));   // contact.id → 'owner' | 'member'
    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const linkable = useMemo(
        () => (networkContacts || []).filter((c) => c.omnis_user_id),
        [networkContacts],
    );

    // What the picker LISTS. Selections live in `selected`, so narrowing this
    // never drops anybody from the roster.
    const visible = useMemo(
        () => linkable.filter((c) => contactMatchesQuery(c, query)),
        [linkable, query],
    );

    // Reset when opened.
    useEffect(() => {
        if (open) {
            setStep('form'); setName(''); setDescription('');
            setSelected(new Set()); setRoles({}); setQuery('');
            setBusy(false); setError(null);
        }
    }, [open]);

    if (!open) return null;

    const toggleMember = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // Un-ticking drops any role choice with it, so re-ticking someone starts
    // from the Member default rather than silently restoring a forgotten Owner.
    const roleFor = (id) => roles[id] || DEFAULT_TEAM_ROLE;
    const setRole = (id, role) => setRoles((prev) => ({ ...prev, [id]: role }));
    const clearRole = (id) => setRoles((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev }; delete next[id]; return next;
    });
    const onToggle = (id) => {
        if (selected.has(id)) clearRole(id);
        toggleMember(id);
    };

    const selectedContacts = linkable.filter((c) => selected.has(c.id));

    const goConfirm = () => {
        if (!name.trim()) { setError(t('teamNameRequired')); return; }
        setError(null);
        setStep('confirm');
    };

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            // Role-carrying shape (part 2). `member_user_ids` stays on the wire
            // as the back-compat mirror for anything still reading it.
            const members = selectedContacts
                .filter((c) => c.omnis_user_id)
                .map((c) => ({ user_id: c.omnis_user_id, role: roleFor(c.id) }));
            const member_user_ids = members.map((m) => m.user_id);
            // No `color` key: the create interface offers no colour choice (#303),
            // so the BE stores NULL and renders fall back to the neutral dot.
            const res = await teamService.createTeam({
                name: name.trim(),
                description: description.trim() || undefined,
                members,
                member_user_ids,
            });
            emitTeamChanged({ team: res.team, type: 'team_created' });
            onCreated?.(res.team);
            onClose?.();
        } catch (err) {
            const failures = err?.body?.error?.failures;
            if (Array.isArray(failures) && failures.length) {
                const names = failures.map((f) => f.email || f.user_id).join(', ');
                setError(t('teamCreateNotLinked', { names }));
            } else {
                setError(userErrorMessage(err, t));
            }
            setBusy(false);
            setStep('form');
        }
    };

    return (
        <Modal onClose={onClose} zIndex="z-[88]" layout="column" cardClassName="max-h-[85dvh]" cardTestId="team-create-dialog">
                <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                    <h3 className="text-base font-semibold text-white">
                        {step === 'confirm' ? t('teamCreateConfirmTitle') : t('teamCreateTitle')}
                    </h3>
                    <button onClick={onClose} aria-label={t('commonClose', 'Close')} className="text-pai-faint hover:text-white p-1 transition-colors">
                        <XIcon size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && <div className="text-xs text-pai-red">{error}</div>}

                    {step === 'form' ? (
                        <>
                            <label className="block">
                                <span className="text-[11px] font-medium text-pai-meta">{t('teamFieldName')}</span>
                                <input
                                    data-testid="team-create-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={t('teamFieldNamePlaceholder')}
                                    className="mt-1 w-full px-2.5 py-1.5 text-sm pai-input"
                                />
                            </label>
                            <label className="block">
                                {/* Label carries the "(optional)" marker (#303) — i18n en/es. */}
                                <span data-testid="team-create-description-label" className="text-[11px] font-medium text-pai-meta">{t('teamFieldDescription')}</span>
                                <input
                                    data-testid="team-create-description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="mt-1 w-full px-2.5 py-1.5 text-sm pai-input"
                                />
                            </label>
                            <div>
                                <span className="text-[11px] font-medium text-pai-meta">{t('teamFieldMembers')}</span>
                                {/* Search first, then the (filtered) network. The
                                    icon sits in a bare positioning wrapper — no
                                    background, no border — so it adds no
                                    decorative node to the dialog. */}
                                <div className="relative mt-1">
                                    <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-pai-faint pointer-events-none" />
                                    <input
                                        data-testid="team-create-search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder={t('teamMemberSearchPlaceholder')}
                                        aria-label={t('teamMemberSearchPlaceholder')}
                                        className="w-full pl-7 pr-2.5 py-1.5 text-sm pai-input"
                                    />
                                </div>
                                <div data-testid="team-create-members" className="mt-1 space-y-1 max-h-48 overflow-y-auto border border-pai-subtle rounded-row p-1">
                                    {linkable.length === 0 ? (
                                        <p className="px-2 py-2 text-xs text-pai-faint italic">{t('teamCreateNoNetwork')}</p>
                                    ) : visible.length === 0 ? (
                                        <p data-testid="team-create-no-matches" className="px-2 py-2 text-xs text-pai-faint italic">
                                            {t('teamMemberSearchNoMatch')}
                                        </p>
                                    ) : visible.map((c, i) => (
                                        // The role select is a SIBLING of the label,
                                        // never inside it: a <select> is labelable, so
                                        // nesting it would steal the label's click from
                                        // the checkbox and break row-toggling.
                                        <div key={c.id} className="flex items-center gap-1.5 pr-1">
                                            <label
                                                data-testid={`team-create-member-${i}`}
                                                className="min-w-0 flex-1 flex items-center gap-2 px-2 py-1.5 rounded-chip hover:bg-white/[0.05] cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(c.id)}
                                                    onChange={() => onToggle(c.id)}
                                                    className="accent-white"
                                                />
                                                <Ball label={c.name} />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm text-white" title={c.name}>{c.name}</span>
                                                    {/* Shown because it is searchable —
                                                        the user needs to see the field
                                                        they can match on. */}
                                                    {c.email && (
                                                        <span className="block truncate text-[11px] text-pai-meta" title={c.email}>{c.email}</span>
                                                    )}
                                                </span>
                                            </label>
                                            {selected.has(c.id) && (
                                                <RoleSelect
                                                    testId={`team-create-role-${i}`}
                                                    value={roleFor(c.id)}
                                                    onChange={(role) => setRole(c.id, role)}
                                                    label={t('teamRoleFor', { name: c.name })}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div data-testid="team-create-roster" className="space-y-3">
                            <p className="text-sm text-pai-secondary">
                                {t('teamCreateConfirmPrompt', { name: name.trim(), n: selectedContacts.length })}
                            </p>
                            <div className="rounded-row border border-pai-subtle divide-y divide-white/[0.06]">
                                <div className="flex items-center gap-2 px-3 py-2">
                                    <span className="text-sm font-medium text-white">{name.trim()}</span>
                                </div>
                                {selectedContacts.length === 0 ? (
                                    <p className="px-3 py-2 text-xs text-pai-faint italic">{t('teamCreateRosterEmpty')}</p>
                                ) : selectedContacts.map((c, i) => (
                                    // Roles stay EDITABLE here: this step is the
                                    // roster review, so the last look at who is
                                    // joining is also the last chance to fix what
                                    // they can do. Same `roles` state as the
                                    // picker — one source of truth, two views.
                                    <div key={c.id} data-testid="team-create-roster-row" data-role={roleFor(c.id)} className="flex items-center gap-2 px-3 py-2">
                                        <Ball label={c.name} />
                                        <span className="min-w-0 flex-1 truncate text-sm text-white">{c.name}</span>
                                        <RoleSelect
                                            testId={`team-create-roster-role-${i}`}
                                            value={roleFor(c.id)}
                                            onChange={(role) => setRole(c.id, role)}
                                            label={t('teamRoleFor', { name: c.name })}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 p-4 border-t border-pai-subtle">
                    {step === 'confirm' && (
                        <button
                            onClick={() => setStep('form')}
                            disabled={busy}
                            className="px-3 py-1.5 text-sm text-pai-secondary hover:bg-white/[0.07] rounded-chip disabled:opacity-50"
                        >
                            {t('teamCreateBack')}
                        </button>
                    )}
                    {step === 'form' ? (
                        <button
                            data-testid="team-create-next"
                            onClick={goConfirm}
                            className="pai-btn-primary"
                        >
                            {t('teamCreateNext')}
                        </button>
                    ) : (
                        <button
                            data-testid="team-create-confirm"
                            onClick={submit}
                            disabled={busy}
                            className="pai-btn-primary disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                            {t('teamCreateConfirmBtn')}
                        </button>
                    )}
                </div>
            </Modal>
    );
};

export default TeamRow;
